'use client';

import {
	forwardRef,
	useRef,
	useImperativeHandle,
	useState,
	useEffect,
	useMemo,
} from 'react';
import Image from 'next/image';
import { useUi } from '@weareconceptstudio/core';

//* Styles
import ImageStyle from './style';

const CustomImage = forwardRef((props, ref) => {
	const {
		src,
		alt,
		className,
		width,
		height,
		priority = false,
		double,
		onLoad,
		isOpacityZero = false,
		onClick,
		fit = 'cover',
		unoptimized = false,
	} = props;

	const { winWidth } = useUi();
	const containerRef = useRef(null);

	/* ---------------- STATE ---------------- */

	const [loaded, setLoaded] = useState(false);
	const [dimensions, setDimensions] = useState(null);

	/* ---------------- RESPONSIVE FILE ---------------- */

	const mobileFile = useMemo(() => {
		if (!double) return null;

		const [desktop, mobile] = double;

		if (!mobile?.src) return desktop;

		return winWidth >= 1280 ? desktop : mobile;
	}, [double, winWidth]);

	/* ---------------- REF ---------------- */

	useImperativeHandle(ref, () => containerRef.current);

	/* ---------------- SIZE OBSERVER ---------------- */

	useEffect(() => {
		if (unoptimized) return;
		if (!containerRef.current) return;

		const updateSize = () => {
			const rect = containerRef.current?.getBoundingClientRect();

			if (rect?.width > 0) {
				setDimensions({
					width: Math.round(rect.width),
					height: Math.round(rect.height),
				});
			}
		};

		updateSize();

		const observer = new ResizeObserver(updateSize);
		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, [unoptimized]);

	/* ---------------- OPTIMIZED SRC ---------------- */

	const optimizedSrc = useMemo(() => {
		const finalSrc =
			src || mobileFile?.src || '/images/default.jpeg';

		if (
			unoptimized ||
			!dimensions?.width ||
			!dimensions?.height ||
			finalSrc.toLowerCase().endsWith('.svg')
		) {
			return finalSrc;
		}

		return `/api/image?src=${encodeURIComponent(
			finalSrc
		)}&w=${dimensions.width}&h=${dimensions.height}&fit=${fit}`;
	}, [
		src,
		mobileFile?.src,
		dimensions?.width,
		dimensions?.height,
		fit,
		unoptimized,
	]);

	/* ---------------- LOAD HANDLER ---------------- */

	const handleLoad = () => setLoaded(true);

	useEffect(() => {
		onLoad?.(loaded);
	}, [loaded, onLoad]);

	/* ---------------- RENDER ---------------- */

	return (
		<ImageStyle
			ref={containerRef}
			onClick={onClick}
			className={`image-cont ${className || ''}`}
			style={{ width, height, position: 'relative' }}
		>
			<Image
				fill
				draggable={false}
				priority={priority}
				unoptimized={unoptimized}
				onLoad={handleLoad}
				sizes="100vw"
				alt={alt || mobileFile?.alt || 'image'}
				loading={priority ? 'eager' : 'lazy'}
				src={optimizedSrc}
				style={{
					opacity: loaded ? (isOpacityZero ? 0 : 1) : 0,
					objectFit: fit,
					transition: 'opacity 0.3s ease',
				}}
			/>
		</ImageStyle>
	);
});

CustomImage.displayName = 'CustomImage';

export default CustomImage;